import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  TrendingUp,
  BarChart3,
  Brain,
  Target,
  Mail,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";
import LoadingSpinner from "../components/LoadingSpinner";

/**
 * Login Page Component
 * Shows sign-in options and app features for unauthenticated users
 */
const Login = () => {
  const { user, loading, signIn, devLogin, devRegister } = useAuth();
  const [authMethod, setAuthMethod] = useState("email"); // 'email' or 'google'
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    displayName: "",
  });

  // Redirect if already authenticated
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);

    try {
      if (isRegisterMode) {
        await devRegister(
          formData.email,
          formData.password,
          formData.displayName,
        );
      } else {
        await devLogin(formData.email, formData.password);
      }
    } catch (error) {
      // Error handling is done in AuthContext
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setAuthLoading(true);
    try {
      await signIn();
    } catch (error) {
      // Error handling is done in AuthContext
    } finally {
      setAuthLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const features = [
    {
      icon: BarChart3,
      title: "Unified Dashboard",
      description:
        "View all your marketing and sales data in one place with beautiful visualizations.",
    },
    {
      icon: Brain,
      title: "AI-Powered Insights",
      description:
        "Get personalized marketing strategies and recommendations powered by advanced AI.",
    },
    {
      icon: Target,
      title: "Goal Tracking",
      description:
        "Set and track your business goals with automatic progress monitoring.",
    },
    {
      icon: TrendingUp,
      title: "Performance Analytics",
      description:
        "Deep dive into your metrics with advanced analytics and trend analysis.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-blue-50">
      <div className="flex flex-col lg:flex-row">
        {/* Left side - Hero content */}
        <div className="flex-1 flex flex-col justify-center px-8 py-12 lg:px-12">
          <div className="max-w-md mx-auto lg:max-w-none">
            {/* Logo */}
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-xl">C</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">
                CRO Agent
              </span>
            </div>

            {/* Hero text */}
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Unify Your
              <span className="text-primary-600"> Marketing Data</span>
            </h1>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Connect all your marketing and sales platforms, visualize
              performance, and get AI-powered strategies to grow your business.
            </p>

            {/* Auth method tabs */}
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-6">
              <button
                onClick={() => {
                  setAuthMethod("email");
                  setFormData({ email: "", password: "", displayName: "" });
                }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${authMethod === "email"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                Email
              </button>
              <button
                onClick={() => setAuthMethod("google")}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${authMethod === "google"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                Google
              </button>
            </div>

            {authMethod === "email" ? (
              <>
                {/* Email/Password Auth Form */}
                <form onSubmit={handleEmailAuth} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  {isRegisterMode && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Display Name
                      </label>
                      <input
                        type="text"
                        name="displayName"
                        required
                        value={formData.displayName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                        placeholder="Your Name"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        required
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                        placeholder="Password (min. 6 characters)"
                        minLength="6"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={authLoading}
                    className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                  >
                    {authLoading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Please wait...</span>
                      </div>
                    ) : isRegisterMode ? (
                      "Create Account"
                    ) : (
                      "Sign In"
                    )}
                  </button>
                </form>

                <div className="mt-4 text-center">
                  <button
                    onClick={() => {
                      setIsRegisterMode(!isRegisterMode);
                      setFormData({ email: "", password: "", displayName: "" });
                    }}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    {isRegisterMode
                      ? "Already have an account? Sign in"
                      : "Need an account? Sign up"}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Google Auth */}
                <button
                  onClick={handleGoogleAuth}
                  disabled={authLoading}
                  className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-medium py-3 px-8 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl"
                >
                  {authLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Please wait...</span>
                    </div>
                  ) : (
                    <>
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="currentColor"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      <span>Continue with Google</span>
                    </>
                  )}
                </button>

                <p className="mt-4 text-sm text-gray-500 text-center">
                  Sign in with your Google account to get started quickly
                </p>
              </>
            )}

            <p className="mt-6 text-sm text-gray-500 text-center lg:text-left">
              Free to get started • No credit card required
            </p>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600 text-center lg:text-left">
                Want to try first?{" "}
                <Link
                  to="/analyze"
                  className="text-primary-600 hover:text-primary-700 font-semibold"
                >
                  Start a free analysis
                </Link>{" "}
                without signing up
              </p>
            </div>
          </div>
        </div>

        {/* Right side - Features */}
        <div className="flex-1 bg-white lg:bg-gray-50 px-8 py-12 lg:px-12">
          <div className="max-w-md mx-auto lg:max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              Everything you need to grow
            </h2>

            <div className="space-y-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;

                return (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Social proof */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-4">
                Trusted by marketing teams at:
              </p>
              <div className="flex items-center space-x-8 opacity-60">
                <div className="text-lg font-bold">ACME Corp</div>
                <div className="text-lg font-bold">TechStart</div>
                <div className="text-lg font-bold">GrowthCo</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
